import { zodResolver } from '@hookform/resolvers/zod';
import { CrewMerchantUi } from 'corso-types';
import { useForm } from 'react-hook-form';
import { Action, Modal, TextInput } from 'corso-admin-ui';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useShippingClaimUpdateCustomerInfo } from '~/features/shippingClaimReview/hooks/useShippingClaimUpdateCustomerInfo';

type ShippingClaimUpdateCustomerInfo =
  CrewMerchantUi.ShippingClaimUpdateCustomerInfo;

export function EditShippingClaimCustomerInfo({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const shippingClaim = useShippingClaim();
  const { mutateAsync: updateCustomerInfo, isPending } =
    useShippingClaimUpdateCustomerInfo();

  const formId = 'edit-shipping-claim-customer-info-form';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CrewMerchantUi.shippingClaimUpdateCustomerInfoSchema),
    // `values` keeps the form in step with the claim after a save, `defaultValues` would not
    values: {
      firstName: shippingClaim.shippingAddress?.firstName ?? '',
      lastName: shippingClaim.shippingAddress?.lastName ?? '',
      line1: shippingClaim.shippingAddress?.line1 ?? '',
      line2: shippingClaim.shippingAddress?.line2 ?? undefined,
      city: shippingClaim.shippingAddress?.city ?? '',
      stateOrProvinceCode:
        shippingClaim.shippingAddress?.stateOrProvinceCode ?? undefined,
      postalCode: shippingClaim.shippingAddress?.postalCode ?? '',
      countryCode: shippingClaim.shippingAddress?.countryCode ?? '',
      phone: shippingClaim.shippingAddress?.phone ?? undefined,
    } satisfies ShippingClaimUpdateCustomerInfo,
  });

  const closeAndReset = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      title="Edit Customer"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit(async (data) => {
            await updateCustomerInfo(data);
            closeAndReset();
          })(e).catch((error: unknown) =>
            console.error('Failed to update customer info', error),
          );
        }}
        className="flex flex-col gap-4"
      >
        <TextInput
          id="edit-shipping-claim-first-name"
          label="First Name"
          autoComplete="first-name"
          {...register('firstName')}
          error={errors.firstName?.message}
        />
        <TextInput
          id="edit-shipping-claim-last-name"
          label="Last Name"
          autoComplete="last-name"
          {...register('lastName')}
          error={errors.lastName?.message}
        />
        <TextInput
          id="edit-shipping-claim-phone"
          label="Phone"
          autoComplete="tel"
          {...register('phone')}
          error={errors.phone?.message}
        />
        <TextInput
          id="edit-shipping-claim-line1"
          label="Street Address"
          autoComplete="address-line1"
          {...register('line1')}
          error={errors.line1?.message}
        />
        <TextInput
          id="edit-shipping-claim-line2"
          label="Street Address Line 2"
          autoComplete="address-line2"
          {...register('line2')}
          error={errors.line2?.message}
        />
        <TextInput
          id="edit-shipping-claim-city"
          label="City"
          autoComplete="address-level2"
          {...register('city')}
          error={errors.city?.message}
        />
        <TextInput
          id="edit-shipping-claim-state"
          label="State or Province Code"
          autoComplete="address-level1"
          {...register('stateOrProvinceCode')}
          error={errors.stateOrProvinceCode?.message}
        />
        <TextInput
          id="edit-shipping-claim-postal-code"
          label="Postal Code"
          autoComplete="postal-code"
          {...register('postalCode')}
          error={errors.postalCode?.message}
        />
        <TextInput
          id="edit-shipping-claim-country"
          label="Country Code"
          autoComplete="country"
          {...register('countryCode')}
          error={errors.countryCode?.message}
        />
      </form>
    </Modal>
  );
}
